import React, { useState, Component } from 'react';
import { Edit2, Flame, TrendingUp, Award, Calendar, Shield, Activity, FileText, MessageSquare, Wand2, Copy, CheckCircle2 } from 'lucide-react';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useScans } from '../hooks/useScans';
import { useMessageRewriter } from '../hooks/useMessageRewriter';
export function ProfilePage() {
  const {
    user,
    loading: authLoading
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    scans,
    loading
  } = useScans(user?.id);
  const {
    history: rewrites,
    loadingHistory: loadingRewrites
  } = useMessageRewriter();
  const [activeTab, setActiveTab] = useState<'activity' | 'scans' | 'rewrites'>('activity');
  const [copiedRewriteId, setCopiedRewriteId] = useState<string | null>(null);
  const handleCopyRewrite = async (text: string, id?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedRewriteId(id || null);
      showToast('Rewrite copied to clipboard!', 'success');
      setTimeout(() => setCopiedRewriteId(null), 2000);
    } catch {
      showToast('Failed to copy text', 'error');
    }
  };
  if (authLoading) {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505] relative overflow-hidden" data-id="element-9900">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] opacity-50 pointer-events-none" data-id="element-9901" />
        <WebsiteHeader data-id="element-9902" />
        <div className="flex items-center justify-center min-h-[60vh]" data-id="element-9903">
          <div className="text-center" data-id="element-9904">
            <div className="w-12 h-12 border-4 border-gray-800 border-t-[#FF0000] rounded-full animate-spin mx-auto mb-4" data-id="element-9905"></div>
            <p className="text-gray-500 font-mono text-xs uppercase tracking-widest" data-id="element-9906">
              Loading profile data...
            </p>
          </div>
        </div>
      </div>;
  }
  if (!user) {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505]" data-id="element-9907">
        <WebsiteHeader data-id="element-9908" />
        <div className="flex items-center justify-center min-h-[60vh]" data-id="element-9909">
          <div className="text-center" data-id="element-9910">
            <p className="text-gray-500 mb-4 font-mono" data-id="element-9911">
              Unable to load profile
            </p>
            <Link to="/dashboard" className="text-[#FF0000] font-bold hover:underline uppercase tracking-wide text-sm" data-id="element-9912">
              Return to Dashboard
            </Link>
          </div>
        </div>
      </div>;
  }
  const firstName = user?.user_metadata?.firstName || 'User';
  const lastName = user?.user_metadata?.lastName || '';
  const fullName = user?.user_metadata?.fullName || `${firstName} ${lastName}`.trim();
  const userEmail = user?.email || '';
  const userInitial = firstName.charAt(0).toUpperCase();
  const joinDate = user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  }) : 'Recently';
  const completedScans = scans.filter(s => s.status === 'completed');
  const totalScans = completedScans.length;
  const avgScore = totalScans > 0 ? Math.round(completedScans.reduce((sum, s) => sum + (s.score || 0), 0) / totalScans) : null;
  const bestScore = totalScans > 0 ? Math.min(...completedScans.map(s => s.score || 100)) : null;
  const daysSinceLastScan = scans.length > 0 ? Math.floor((new Date().getTime() - new Date(scans[0].created_at).getTime()) / (1000 * 60 * 60 * 24)) : 0;
  const streak = daysSinceLastScan === 0 && scans.length > 0 ? 1 : 0;
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] relative overflow-hidden" data-id="element-9913">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-9914" />

      <WebsiteHeader data-id="element-9915" />

      <div className="max-w-4xl mx-auto w-full relative z-10" data-id="element-9916">
        <main className="px-6 pt-12 pb-24" data-id="element-9917">
          {/* Page Header */}
          <div className="text-center mb-10" data-id="element-9918">
            <div className="inline-flex items-center gap-2 mb-4 bg-[#FF0000]/10 border border-[#FF0000]/30 px-3 py-1" data-id="element-9919">
              <Shield size={14} className="text-[#FF0000]" data-id="element-9920" />
              <span className="text-[#FF0000] font-mono text-xs tracking-widest uppercase font-bold" data-id="element-9921">
                Agent Profile
              </span>
            </div>
            <h1 className="text-3xl font-black text-white mb-2 uppercase tracking-tight" data-id="element-9922">
              User Dossier
            </h1>
            <p className="text-gray-400 font-mono text-sm" data-id="element-9923">
              Activity logs and performance metrics
            </p>
          </div>

          {/* Profile Card */}
          <div className="bg-[#0a0a0a] p-8 mb-8 border border-gray-800 relative group" data-id="element-9924">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9925" />
            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9926" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9927" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9928" />

            <div className="flex items-start justify-between mb-6" data-id="element-9929">
              <div className="flex items-center gap-6" data-id="element-9930">
                <div className="w-20 h-20 bg-[#050505] border border-gray-800 flex items-center justify-center text-white text-3xl font-black shadow-[0_0_20px_rgba(255,0,0,0.1)] relative" data-id="element-9931">
                  {userInitial}
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-[#FF0000] border border-black" data-id="element-9932"></div>
                </div>
                <div data-id="element-9933">
                  <h2 className="font-bold text-2xl text-white uppercase tracking-wide mb-1" data-id="element-9934">
                    {fullName}
                  </h2>
                  <p className="text-gray-500 text-sm font-mono mb-2" data-id="element-9935">
                    {userEmail}
                  </p>
                  <div className="flex items-center gap-2" data-id="element-9936">
                    <Calendar size={12} className="text-gray-600" data-id="element-9937" />
                    <p className="text-gray-600 text-xs font-mono uppercase" data-id="element-9938">
                      Active Since: {joinDate}
                    </p>
                  </div>
                </div>
              </div>
              <Link to="/settings" data-id="element-9939">
                <button className="p-2 hover:bg-gray-900 border border-transparent hover:border-gray-800 transition-all group/edit" data-id="element-9940">
                  <Edit2 size={18} className="text-gray-600 group-hover/edit:text-white" data-id="element-9941" />
                </button>
              </Link>
            </div>

            {streak > 0 && <div className="bg-[#FFD700]/5 border border-[#FFD700]/20 p-3 mb-4 flex items-center gap-3" data-id="element-9942">
                <Flame size={16} className="text-[#FFD700]" data-id="element-9943" />
                <span className="font-bold text-[#FFD700] text-sm uppercase tracking-wide" data-id="element-9944">
                  {streak} day streak active
                </span>
              </div>}

            <div className="h-px bg-gray-900 my-4" data-id="element-9945" />

            <p className="text-gray-500 text-xs font-mono uppercase tracking-widest" data-id="element-9946">
              Status: <span className="text-green-500" data-id="element-9947">Operational</span>
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-9948">
            <StatCard icon={<TrendingUp size={16} className="text-blue-500" data-id="element-9950" />} value={totalScans.toString()} label="Total Scans" color="blue" data-id="element-9949" />
            <StatCard icon={<Activity size={16} className="text-purple-500" data-id="element-9952" />} value={avgScore !== null ? avgScore.toString() : '—'} label="Avg. Toxicity" color="purple" data-id="element-9951" />
            <StatCard icon={<Award size={16} className="text-green-500" data-id="element-9954" />} value={bestScore !== null ? bestScore.toString() : '—'} label="Best Score" color="green" data-id="element-9953" />
            <StatCard icon={<Flame size={16} className="text-orange-500" data-id="element-9956" />} value={streak.toString()} label="Day Streak" color="orange" data-id="element-9955" />
          </div>

          {/* Tabs */}
          <div className="bg-[#0a0a0a] p-1 mb-8 border border-gray-800 flex gap-1" data-id="element-9957">
            <button onClick={() => setActiveTab('activity')} className={`flex-1 py-3 px-4 text-xs font-bold uppercase tracking-wider transition-all ${activeTab === 'activity' ? 'bg-[#FF0000] text-white' : 'text-gray-500 hover:text-white hover:bg-gray-900'}`} data-id="element-9958">
              Recent Activity
            </button>
            <button onClick={() => setActiveTab('scans')} className={`flex-1 py-3 px-4 text-xs font-bold uppercase tracking-wider transition-all ${activeTab === 'scans' ? 'bg-[#FF0000] text-white' : 'text-gray-500 hover:text-white hover:bg-gray-900'}`} data-id="element-9959">
              All Scans ({totalScans})
            </button>
            <button onClick={() => setActiveTab('rewrites')} className={`flex-1 py-3 px-4 text-xs font-bold uppercase tracking-wider transition-all ${activeTab === 'rewrites' ? 'bg-[#FF0000] text-white' : 'text-gray-500 hover:text-white hover:bg-gray-900'}`} data-id="element-9960">
              Rewrites
            </button>
          </div>

          {/* Content */}
          {activeTab === 'rewrites' ? loadingRewrites ? <div className="bg-[#0a0a0a] p-12 border border-gray-800 text-center relative" data-id="element-9961">
                <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-600" data-id="element-9962" />
                <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-600" data-id="element-9963" />
                <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-600" data-id="element-9964" />
                <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-600" data-id="element-9965" />
                <div className="inline-block w-8 h-8 border-2 border-gray-800 border-t-[#FF0000] rounded-full animate-spin mb-4" data-id="element-9966"></div>
                <p className="text-gray-500 text-xs font-mono uppercase" data-id="element-9967">
                  Retrieving rewrites...
                </p>
              </div> : rewrites.length === 0 ? <div className="bg-[#0a0a0a] p-12 border border-gray-800 text-center relative" data-id="element-9968">
                <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600" data-id="element-9969" />
                <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600" data-id="element-9970" />
                <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600" data-id="element-9971" />
                <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600" data-id="element-9972" />
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 mb-6 border border-gray-800" data-id="element-9973">
                  <Wand2 className="w-8 h-8 text-gray-600" data-id="element-9974" />
                </div>
                <h3 className="font-bold text-white mb-2 uppercase tracking-wide" data-id="element-9975">
                  No Rewrites Yet
                </h3>
                <p className="text-gray-500 text-sm mb-8 font-mono max-w-xs mx-auto" data-id="element-9976">
                  Use the Message Rewriter to transform your communication
                  style.
                </p>
                <Link to="/tools/rewriter" className="inline-block bg-[#FF0000] text-white px-6 py-3 font-bold hover:bg-red-600 transition-all shadow-[0_0_15px_rgba(255,0,0,0.3)] uppercase tracking-wider text-sm" data-id="element-9977">
                  Try Rewriter
                </Link>
              </div> : <div className="space-y-3" data-id="element-9978">
                {rewrites.map((rewrite, idx) => {
            const isCopied = copiedRewriteId === (rewrite.id || String(idx));
            return <div key={rewrite.id || idx} className="bg-[#0a0a0a] p-4 border border-gray-800 hover:border-gray-600 transition-all group relative" data-id="element-9979">
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-800 group-hover:bg-[#FF0000] transition-colors" data-id="element-9980" />
                      <div className="pl-3" data-id="element-9981">
                        <div className="flex items-center justify-between mb-2" data-id="element-9982">
                          <div className="flex items-center gap-2" data-id="element-9983">
                            <span className="text-[10px] px-2 py-0.5 bg-[#FF0000]/10 text-[#FF0000] border border-[#FF0000]/30 font-bold uppercase tracking-wider" data-id="element-9984">
                              {rewrite.tone}
                            </span>
                            {rewrite.toxicity_score !== undefined && <span className="text-[10px] text-emerald-500 font-mono" data-id="element-9985">
                                +{rewrite.toxicity_score}% healthier
                              </span>}
                          </div>
                          <div className="flex items-center gap-3" data-id="element-9986">
                            <button onClick={e => {
                      e.stopPropagation();
                      handleCopyRewrite(rewrite.rewritten_text, rewrite.id || String(idx));
                    }} className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-all ${isCopied ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800 border border-gray-700'}`} data-id="element-9987">
                              {isCopied ? <>
                                  <CheckCircle2 size={12} data-id="element-9988" />
                                  Copied
                                </> : <>
                                  <Copy size={12} data-id="element-9989" />
                                  Copy
                                </>}
                            </button>
                            <span className="text-[10px] text-gray-600 font-mono uppercase" data-id="element-9990">
                              {rewrite.created_at ? getTimeAgo(rewrite.created_at) : ''}
                            </span>
                          </div>
                        </div>
                        <p className="text-gray-500 text-xs font-mono mb-1 line-clamp-1" data-id="element-9991">
                          Original: {rewrite.original_text}
                        </p>
                        <p className="text-white text-sm font-mono line-clamp-2" data-id="element-9992">
                          {rewrite.rewritten_text}
                        </p>
                      </div>
                    </div>;
          })}
              </div> : loading ? <div className="bg-[#0a0a0a] p-12 border border-gray-800 text-center relative" data-id="element-9993">
              <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-600" data-id="element-9994" />
              <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-600" data-id="element-9995" />
              <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-600" data-id="element-9996" />
              <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-600" data-id="element-9997" />
              <div className="inline-block w-8 h-8 border-2 border-gray-800 border-t-[#FF0000] rounded-full animate-spin mb-4" data-id="element-9998"></div>
              <p className="text-gray-500 text-xs font-mono uppercase" data-id="element-9999">
                Retrieving data...
              </p>
            </div> : activeTab === 'activity' ? scans.length === 0 ? <EmptyState data-id="element-10000" /> : <div className="space-y-3" data-id="element-10001">
                {scans.slice(0, 5).map(scan => <ActivityItem key={scan.id} scan={scan} data-id="element-10002" />)}
              </div> : scans.length === 0 ? <EmptyState data-id="element-10003" /> : <div className="space-y-3" data-id="element-10004">
              {scans.map(scan => <ActivityItem key={scan.id} scan={scan} showScore data-id="element-10005" />)}
            </div>}
        </main>
      </div>
    </div>;
}
// Helper Components
const StatCard = ({
  icon,
  value,
  label,
  color
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
  color: string;
}) => <div className="bg-[#0a0a0a] p-5 border border-gray-800 relative group hover:border-gray-700 transition-colors" data-id="element-10006">
    {/* Corner Brackets */}
    <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-600 opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-10007" />
    <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-600 opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-10008" />
    <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-600 opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-10009" />
    <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-600 opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-10010" />

    <div className="flex items-center gap-2 mb-3" data-id="element-10011">
      <div className={`w-8 h-8 bg-gray-900 border border-gray-800 flex items-center justify-center`} data-id="element-10012">
        {icon}
      </div>
    </div>
    <div className="text-2xl font-black text-white mb-1 font-mono" data-id="element-10013">{value}</div>
    <div className="text-[10px] text-gray-500 uppercase tracking-widest font-mono" data-id="element-10014">
      {label}
    </div>
  </div>;
const ActivityItem = ({
  scan,
  showScore = false
}: {
  scan: any;
  showScore?: boolean;
}) => <div className="bg-[#0a0a0a] p-4 border border-gray-800 hover:border-gray-600 transition-all group relative" data-id="element-10015">
    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-800 group-hover:bg-[#FF0000] transition-colors" data-id="element-10016" />

    <div className="flex items-center justify-between pl-3" data-id="element-10017">
      <div className="flex-1 min-w-0" data-id="element-10018">
        <p className="font-bold text-white text-sm truncate uppercase tracking-wide mb-1" data-id="element-10019">
          {scan.title}
        </p>
        <p className="text-xs text-gray-500 font-mono" data-id="element-10020">
          {showScore ? `Score: ${scan.score || 'Processing'} • ` : ''}
          {getTimeAgo(scan.created_at)}
        </p>
      </div>
      <Link to={`/analysis?id=${scan.id}`} className="ml-3 bg-gray-900 text-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#FF0000] hover:text-white transition-all border border-gray-800 hover:border-[#FF0000]" data-id="element-10021">
        View
      </Link>
    </div>
  </div>;
const EmptyState = () => <div className="bg-[#0a0a0a] p-12 border border-gray-800 text-center relative" data-id="element-10022">
    {/* Corner Brackets */}
    <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600" data-id="element-10023" />
    <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600" data-id="element-10024" />
    <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600" data-id="element-10025" />
    <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600" data-id="element-10026" />

    <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 mb-6 border border-gray-800" data-id="element-10027">
      <FileText className="w-8 h-8 text-gray-600" data-id="element-10028" />
    </div>
    <h3 className="font-bold text-white mb-2 uppercase tracking-wide" data-id="element-10029">
      No Data Found
    </h3>
    <p className="text-gray-500 text-sm mb-8 font-mono max-w-xs mx-auto" data-id="element-10030">
      Initiate your first scan to generate forensic data.
    </p>
    <Link to="/upload" className="inline-block bg-[#FF0000] text-white px-6 py-3 font-bold hover:bg-red-600 transition-all shadow-[0_0_15px_rgba(255,0,0,0.3)] uppercase tracking-wider text-sm" data-id="element-10031">
      Initiate Scan
    </Link>
  </div>;
function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 60) return `${diffMins} MIN AGO`;
  if (diffHours < 24) return `${diffHours} HR AGO`;
  if (diffDays < 7) return `${diffDays} DAY${diffDays !== 1 ? 'S' : ''} AGO`;
  return `${Math.floor(diffDays / 7)} WK AGO`;
}